/**
 * Labstep
 *
 * @module grid/SmartTable/coldefs/metadata/default
 * @desc Column Definition for metadata default
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import type { ResourceItemData } from 'labstep-web/components/ResourceItem/Action/BulkCreate/Modal/type';
import { ICONS } from 'labstep-web/constants/icons';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type { CellRendererParams } from 'labstep-web/core/DataGrid/types';
import { Icon } from 'labstep-web/core/Icon';
import { GridBatchCreateResourceItemsService } from 'labstep-web/grid/BatchCreateResourceItems/services/grid-batch-create-resource-items-service';
import type { BatchCreateResourceItemsColDef } from 'labstep-web/grid/BatchCreateResourceItems/types';
import type { Metadata } from 'labstep-web/models/metadata';
import { MetadataType } from 'labstep-web/models/metadata/types';

export const getMetadataDefaultColDef = (
  metadata: Metadata,
  data: ResourceItemData[],
  setData: React.Dispatch<React.SetStateAction<ResourceItemData[]>>,
): BatchCreateResourceItemsColDef => {
  const colId = `metadata:${MetadataType.default}:${metadata.guid}`;
  return {
    colId,
    sortable: true,
    headerName: metadata.label || '',
    isRequired: metadata.is_required ?? false,
    headerComponentParams: {
      icon: ICONS.metadata.type.default,
      cornerIcon: ICONS.resource_item.primary,
    },
    editable: (params: EditableCallbackParams) => true,
    valueGetter: (params: ValueGetterParams) => {
      if (!params.node?.data[colId]) {
        return null;
      }

      return params.node.data[colId].value;
    },
    valueSetter: (params: ValueSetterParams) => {
      if (!params.node) {
        return false;
      }

      const row = params.node.data;
      row[colId] =
        GridBatchCreateResourceItemsService.getMetadataBody(
          { value: params.newValue },
          metadata,
        );
      data[params.node.rowIndex!] = row;
      setData(data);
      GridBatchCreateResourceItemsService.removeErrorInData(
        data,
        setData,
        params,
      );
      return true;
    },
    cellRenderer: (params: CellRendererParams<ResourceItemData>) => {
      if (!params.node) {
        return null;
      }

      const err = GridBatchCreateResourceItemsService.findErrorInData(
        data,
        params,
      );

      const metadataValue =
        params.node.data[colId]?.value || metadata.value;
      if (!metadataValue) {
        return (
          <span
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <DataGridPlaceholder
              params={params}
              editable={true}
              placeholder={'Enter'}
            />
            {err && (
              <Icon
                color={'red'}
                name="warning sign"
                popup={{
                  content: err.message,
                }}
              />
            )}
          </span>
        );
      }

      return (
        <span
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {metadataValue}
          {err && (
            <Icon
              color={'red'}
              name="warning sign"
              popup={{
                content: err.message,
              }}
            />
          )}
        </span>
      );
    },
    cellStyle: (params) => {
      if (!params.node) {
        return null;
      }

      const err = GridBatchCreateResourceItemsService.findErrorInData(
        data,
        params,
      );
      if (err) {
        return { backgroundColor: '#F5B1B1' };
      }

      return null;
    },
  };
};
